import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { flat, fromFormattedString, render } from "@xmcl/text-component";

function textComponent(value) {
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
      try { return textComponent(JSON.parse(trimmed)); } catch { /* plain text */ }
    }
    return fromFormattedString(value);
  }
  if (Array.isArray(value)) return { text: "", extra: value.map(textComponent) };
  if (value && typeof value === "object") return {
    ...value,
    text: String(value.text ?? ""),
    extra: Array.isArray(value.extra) ? value.extra.map(textComponent) : []
  };
  return { text: value == null ? "" : String(value) };
}

function MinecraftText({ value, custom = false }) {
  const source = textComponent(value);
  const explicitStyle = typeof value === "object" || (typeof value === "string" && (value.trim().startsWith("{") || value.trim().startsWith("[")));
  const node = render(source);
  const draw = (part, key) => <span key={key} style={{
    ...part.style,
    ...(custom && part.component.italic !== false ? { fontStyle: "italic" } : {}),
    ...(explicitStyle && part.component.italic === false ? { fontStyle: "normal" } : {}),
    ...(/^#[\da-fA-F]{6}$/.test(part.component.color || "") ? { color: part.component.color } : {})
  }}>{part.component.text}{part.children.map(draw)}</span>;
  return draw(node, 0);
}

const ENCHANTMENT_NAMES = {
  sharpness: "锋利", efficiency: "效率", unbreaking: "耐久", mending: "经验修补",
  protection: "保护", fire_protection: "火焰保护", blast_protection: "爆炸保护",
  projectile_protection: "弹射物保护", feather_falling: "摔落缓冲", respiration: "水下呼吸",
  aqua_affinity: "水下速掘", thorns: "荆棘", depth_strider: "深海探索者",
  frost_walker: "冰霜行者", binding_curse: "绑定诅咒", vanishing_curse: "消失诅咒",
  smite: "亡灵杀手", bane_of_arthropods: "节肢杀手", knockback: "击退",
  fire_aspect: "火焰附加", looting: "抢夺", sweeping_edge: "横扫之刃",
  fortune: "时运", silk_touch: "精准采集", power: "力量", punch: "冲击",
  flame: "火矢", infinity: "无限", luck_of_the_sea: "海之眷顾", lure: "饵钓",
  loyalty: "忠诚", impaling: "穿刺", riptide: "激流", channeling: "引雷",
  multishot: "多重射击", quick_charge: "快速装填", piercing: "穿透",
  soul_speed: "灵魂疾行", swift_sneak: "迅捷潜行", wind_burst: "风爆",
  density: "致密", breach: "破甲"
};
const ATTRIBUTE_NAMES = {
  attack_damage: "攻击伤害", attack_speed: "攻击速度", armor: "盔甲", armor_toughness: "盔甲韧性",
  max_health: "最大生命值", movement_speed: "移动速度", mining_efficiency: "挖掘效率",
  block_break_speed: "方块破坏速度", water_movement_efficiency: "水下移动效率",
  oxygen_bonus: "额外氧气", knockback_resistance: "击退抗性", gravity: "重力"
};
const SLOT_NAMES = {
  mainhand: "在主手时：", offhand: "在副手时：", feet: "穿在脚上时：",
  legs: "穿在腿上时：", chest: "穿在身上时：", head: "戴在头上时：",
  hand: "手持时：", armor: "穿戴时："
};

function enchantmentLevel(level) {
  const roman = ["", "I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X"];
  return roman[level] || `enchantment.level.${level}`;
}

export function plainText(value) {
  return flat(textComponent(value)).map(part => part.text).join("");
}

export function getNameValue(item, items, itemNames) {
  const components = item?.components || {};
  return components["minecraft:custom_name"] || components["minecraft:item_name"]
    || itemNames?.[item?.minecraftId?.replace(/^minecraft:/, "")]
    || items?.[item?.id]?.readable || item?.minecraftId || "未知物品";
}

export function getItemName(item, items, itemNames) {
  return plainText(getNameValue(item, items, itemNames));
}

export function hasContainer(item) {
  return Boolean(item && (item.container?.length || /(?:shulker_box|bundle)$/.test(item.minecraftId || "")));
}

export function getContainerSlots(item) {
  const slots = Array(27).fill(null);
  for (const entry of item?.container || []) {
    const slot = Number(entry?.slot);
    if (Number.isInteger(slot) && slot >= 0 && slot < slots.length) slots[slot] = entry.item || null;
  }
  return slots;
}

export function ItemTooltipContent({ item, items, itemNames }) {
  const components = item?.components || {};
  const lore = Array.isArray(components["minecraft:lore"]) ? components["minecraft:lore"] : [];
  const enchantments = [components["minecraft:enchantments"], components["minecraft:stored_enchantments"]]
    .flatMap(value => Object.entries(value?.levels || value || {}).filter(([id, level]) => id !== "show_in_tooltip" && typeof level === "number"));
  const summary = (item?.container || []).filter(entry => entry?.item).slice(0, 5);
  const modifiers = components["minecraft:attribute_modifiers"]?.modifiers || components["minecraft:attribute_modifiers"] || [];
  const attributes = Array.isArray(modifiers) ? modifiers : [];
  const maxDamage = Number(components["minecraft:max_damage"]);
  const damage = Number(components["minecraft:damage"]);
  const groupedAttributes = Object.groupBy ? Object.groupBy(attributes, modifier => modifier.slot || "any")
    : attributes.reduce((groups, modifier) => {
      const slot = modifier.slot || "any";
      (groups[slot] ||= []).push(modifier);
      return groups;
    }, {});
  return <div className="mc-item-tooltip">
    <div className="mc-item-tooltip__name"><MinecraftText value={getNameValue(item, items, itemNames)} custom={components["minecraft:custom_name"] != null} /></div>
    {enchantments.map(([id, level]) => {
      const displayLevel = enchantmentLevel(level);
      const isUntranslatedLevel = displayLevel.startsWith("enchantment.level.");
      return <div className={`mc-item-tooltip__enchant${isUntranslatedLevel ? " mc-item-tooltip__enchant--untranslated" : ""}`} key={id}>
        <span className="mc-item-tooltip__enchant-name">{ENCHANTMENT_NAMES[id.replace(/^minecraft:/, "")] || id.replace(/^minecraft:/, "").replaceAll("_", " ")}</span>
        <span className="mc-item-tooltip__enchant-level">{displayLevel}</span>
      </div>;
    })}
    {lore.map((line, index) => <div className="mc-item-tooltip__lore" key={index}><MinecraftText value={line} /></div>)}
    {Number.isFinite(maxDamage) && maxDamage > 0 && Number.isFinite(damage) && <div>耐久度：{Math.max(0, maxDamage - damage)} / {maxDamage}</div>}
    {Object.entries(groupedAttributes).map(([slot, rows]) => <div className="mc-item-tooltip__attributes" key={slot}>
      <div>{SLOT_NAMES[slot] || "属性："}</div>
      {rows.map((modifier, index) => {
        const id = String(modifier.type || modifier.attribute || "").replace(/^minecraft:/, "");
        const amount = Number(modifier.amount);
        const value = modifier.operation === "add_multiplied_base" || modifier.operation === "add_multiplied_total"
          ? `${Math.round(amount * 100)}%` : String(amount);
        return <div className="mc-item-tooltip__attribute" key={index}>{amount > 0 ? "+" : ""}{value} {ATTRIBUTE_NAMES[id] || id.replaceAll("_", " ")}</div>;
      })}
    </div>)}
    {summary.map((entry, index) => <div key={index}>{getItemName(entry.item, items, itemNames)} × {entry.item.count}</div>)}
    {(item?.container?.length || 0) > 5 && <div className="mc-item-tooltip__muted">还有更多内容...</div>}
    {item?.minecraftId && <><div className="mc-item-tooltip__id">{item.minecraftId}</div><div className="mc-item-tooltip__id">{Object.keys(components).length}个组件</div><div className="mc-item-tooltip__muted">Minecraft</div></>}
  </div>;
}

export default function ItemInspector({ item, items, itemNames, onClose }) {
  const [path, setPath] = useState([item]);
  const currentItem = path[path.length - 1];
  const slots = useMemo(() => getContainerSlots(currentItem), [currentItem]);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    const onKeyDown = event => { if (event.key === "Escape") onClose(); };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose]);

  const chooseItem = (nestedItem, element) => {
    if (!nestedItem) return;
    if (hasContainer(nestedItem)) {
      setPreview(null);
      setPath(previous => [...previous, nestedItem]);
    } else {
      setPreview({ item: nestedItem, rect: element.getBoundingClientRect() });
    }
  };

  if (typeof document === "undefined") return null;
  return createPortal(
    <div className="mc-inventory-inspector__backdrop" onClick={event => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <section className="mc-inventory-inspector" role="dialog" aria-modal="true" aria-label={`${getItemName(currentItem, items, itemNames)}内物品`}>
        <header className="mc-inventory-inspector__header">
          <span><MinecraftText value={getNameValue(currentItem, items, itemNames)} custom={currentItem?.components?.["minecraft:custom_name"] != null} /></span>
          <div className="mc-inventory-inspector__actions">
            {path.length > 1 && <button type="button" onClick={() => { setPreview(null); setPath(previous => previous.slice(0, -1)); }}>返回</button>}
            <button type="button" onClick={onClose} aria-label="关闭容器">×</button>
          </div>
        </header>
        <div className="mc-inventory-inspector__container-grid">
          {slots.map((nestedItem, index) => (
            <div key={index} className={`mc-grid mc-inventory-inspector__slot${nestedItem ? " is-filled" : ""}`}
              role={nestedItem ? "button" : undefined} tabIndex={nestedItem ? 0 : undefined}
              aria-label={nestedItem ? `${getItemName(nestedItem, items, itemNames)}，数量 ${nestedItem.count}` : undefined}
              onMouseEnter={event => nestedItem && setPreview({ item: nestedItem, rect: event.currentTarget.getBoundingClientRect() })}
              onMouseLeave={() => setPreview(null)}
              onClick={event => chooseItem(nestedItem, event.currentTarget)}
              onKeyDown={event => {
                if (nestedItem && (event.key === "Enter" || event.key === " ")) {
                  event.preventDefault();
                  chooseItem(nestedItem, event.currentTarget);
                }
              }}>
              {nestedItem?.id != null && items?.[nestedItem.id]?.texture && <img src={items[nestedItem.id].texture} alt="" className="pixelated" />}
              {nestedItem?.count > 1 && <span className="mc-inventory-inspector__count">{nestedItem.count}</span>}
            </div>
          ))}
        </div>
        {preview && <div className="mc-inventory-inspector__nested-tooltip" style={{
          left: Math.min(preview.rect.right + 8, Math.max(10, window.innerWidth - 290)),
          top: Math.min(preview.rect.top, Math.max(10, window.innerHeight - 190))
        }}><ItemTooltipContent item={preview.item} items={items} itemNames={itemNames} /></div>}
      </section>
    </div>, document.body
  );
}
